import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { extractErrorMessage } from '../../../api/errors';
import { marketplaceApi } from '../../../api/marketplace';
import OrderChat from '../../../components/messaging/OrderChat';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import { useAuth } from '../../../context/AuthContext';

const STAGES = ['placed', 'accepted', 'shipped', 'delivered'];
const STAGE_LABEL = {
  placed: 'Placed',
  accepted: 'Accepted',
  shipped: 'Shipped',
  delivered: 'Delivered',
};
const STATUS_BADGE = {
  placed: 'bg-blue-100 text-blue-700',
  accepted: 'bg-indigo-100 text-indigo-700',
  shipped: 'bg-amber-100 text-amber-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

function formatCurrency(n) {
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
}

function StatusTimeline({ status }) {
  const currentIdx = STAGES.indexOf(status);
  return (
    <div className="flex items-center gap-2 text-xs">
      {STAGES.map((stage, i) => {
        const reached = i <= currentIdx;
        return (
          <React.Fragment key={stage}>
            <div className="flex items-center gap-1.5">
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                  reached ? 'bg-green-700 text-white' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {i + 1}
              </span>
              <span className={reached ? 'font-medium text-gray-900' : 'text-gray-400'}>
                {STAGE_LABEL[stage]}
              </span>
            </div>
            {i < STAGES.length - 1 && (
              <span className={`h-px w-6 ${i < currentIdx ? 'bg-green-700' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function MyOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [params, setParams] = useSearchParams();
  const justPlacedId = params.get('placed');
  const [chatOpenId, setChatOpenId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await marketplaceApi.buyerOrders();
        setOrders(data);
      } catch (err) {
        setError(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <p className="text-sm text-gray-500">Loading orders…</p>;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">My orders</h1>
        <p className="mt-1 text-sm text-gray-600">
          Track the orders you've placed with FPOs.
        </p>
      </div>

      {justPlacedId && (
        <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          <span>✓ Order #{justPlacedId} placed. The FPO will accept it shortly.</span>
          <button
            type="button"
            onClick={() => setParams({})}
            className="text-xs font-semibold text-green-700 hover:text-green-800"
          >
            Dismiss
          </button>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {orders.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-gray-500">You haven't placed any orders yet.</p>
          <div className="mt-4">
            <Link to="/app/marketplace">
              <Button>Browse marketplace</Button>
            </Link>
          </div>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {orders.map((order) => (
            <Card key={order.id} className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-500">Order #{order.id}</span>
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${STATUS_BADGE[order.status]}`}
                    >
                      {order.status}
                    </span>
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${
                        order.payment_status === 'paid'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      Payment: {order.payment_status}
                    </span>
                  </div>
                  <h3 className="mt-1 text-base font-semibold text-gray-900">
                    From {order.organisation_name}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Placed {new Date(order.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-green-800">{formatCurrency(order.total)}</p>
                  <p className="text-xs text-gray-500">
                    {order.items.length} item{order.items.length === 1 ? '' : 's'}
                  </p>
                </div>
              </div>

              {order.status !== 'cancelled' && (
                <div className="mt-4 overflow-x-auto">
                  <StatusTimeline status={order.status} />
                </div>
              )}

              <ul className="mt-4 flex flex-col gap-1 text-sm">
                {order.items.map((it) => (
                  <li key={it.id} className="flex justify-between text-gray-700">
                    <span>
                      {it.listing_title_snapshot}
                      <span className="ml-1 text-gray-500">
                        · {Number(it.quantity).toFixed(2)} {it.unit_snapshot}
                      </span>
                    </span>
                    <span className="font-medium text-gray-900">{formatCurrency(it.line_total)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => setChatOpenId(chatOpenId === order.id ? null : order.id)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  {chatOpenId === order.id ? 'Hide messages' : 'Message the FPO'}
                </button>
              </div>
              {chatOpenId === order.id && (
                <div className="mt-4 border-t border-gray-100 pt-4">
                  <OrderChat
                    orderId={order.id}
                    currentUserId={user.id}
                    allowCounterOffer={true}
                  />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
